/**
 * EIR slide-panel view of a single founder's data room, with Overview and Notes
 * tabs. Overview shows per-category progress, lets the EIR set per-category
 * deadlines, and downloads category zips; Notes embeds NotesTab for the founder.
 *
 * Categories are mirrored into local state so deadline edits reflect immediately
 * without refetching the whole panel.
 */
import React, { useState, useEffect } from 'react';
import SlidePanel from './SlidePanel';
import CircularProgress from './CircularProgress';
import NotesTab from './NotesTab';
import { setDataRoomDeadline, setDataRoomItemReview } from '../services/apiService';

// Per-item EIR sign-off: Not reviewed / Complete / Needs work, plus an optional
// note. Independent of upload — an EIR can mark work done that isn't uploaded.
const REVIEW_STYLE = {
  none:       { background: 'var(--color-frost)', color: 'var(--color-forest)', border: '1.5px solid var(--color-mint-dark)' },
  complete:   { background: '#dcfce7', color: '#15803d', border: '1.5px solid #86efac' },
  needs_work: { background: '#fef3c7', color: '#b45309', border: '1.5px solid #fcd34d' },
};

function ItemReviewControl({ founderUserId, item }) {
  const [status, setStatus] = useState(item.review?.status || 'none');
  const [note, setNote] = useState(item.review?.note || '');
  const [savedNote, setSavedNote] = useState(item.review?.note || '');

  async function persist(nextStatus, nextNote) {
    try { await setDataRoomItemReview(founderUserId, item.id, nextStatus, nextNote); }
    catch { /* silent — reloads correctly on next open */ }
  }

  function onStatusChange(e) {
    const v = e.target.value;
    setStatus(v);
    const nextNote = v === 'none' ? '' : note;
    if (v === 'none') { setNote(''); setSavedNote(''); }
    persist(v, nextNote);
  }

  function onNoteBlur() {
    if (status !== 'none' && note !== savedNote) {
      setSavedNote(note);
      persist(status, note);
    }
  }

  return (
    <div style={reviewStyles.wrap} onClick={(e) => e.stopPropagation()}>
      <select value={status} onChange={onStatusChange} style={{ ...reviewStyles.select, ...REVIEW_STYLE[status] }}>
        <option value="none">— Not reviewed</option>
        <option value="complete">✓ Complete</option>
        <option value="needs_work">Needs work</option>
      </select>
      {status !== 'none' && (
        <input
          style={reviewStyles.note}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={onNoteBlur}
          placeholder="Add a note (optional)"
        />
      )}
    </div>
  );
}

// Fetches a category's documents as a zip and triggers a browser download.
// Uses fetch+blob (not a plain link) so the auth cookie is sent; fails silently.
async function downloadCategoryZip(founderUserId, categoryId, filename) {
  try {
    const resp = await fetch(`/api/vr/eir/founders/${founderUserId}/data-room/export/${categoryId}`, { credentials: 'include' });
    if (!resp.ok) return;
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {
    // silent
  }
}

// Opens a stored file in a new tab. Same-origin "/..." paths are fetched with
// the auth cookie and shown via a blob URL; absolute URLs (e.g. S3 presigned)
// are opened directly.
async function openFile(url) {
  if (!url) return;
  try {
    if (url.startsWith('/')) {
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) return;
      const blob = await resp.blob();
      window.open(URL.createObjectURL(blob), '_blank', 'noopener');
    } else {
      window.open(url, '_blank', 'noopener');
    }
  } catch {
    // file not accessible
  }
}

const tabStyles = {
  tabBar:    { display: 'flex', gap: 4, marginBottom: 16 },
  tab:       { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, padding: '6px 16px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-mint-dark)', background: 'var(--color-frost)', color: 'var(--color-forest)', cursor: 'pointer' },
  tabActive: { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, padding: '6px 16px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-forest)', background: 'var(--color-forest)', color: '#fff', cursor: 'pointer' },
};

export default function EIRDataRoomPopUp({ open, onClose, founder, data, loading }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [localCategories, setLocalCategories] = useState([]);

  // Reset to the Overview tab whenever a different founder is opened.
  useEffect(() => { setActiveTab('overview'); }, [founder?.id]);

  useEffect(() => {
    if (data?.categories) setLocalCategories(data.categories);
  }, [data]);

  // Persist a new category deadline, then optimistically patch local state.
  // On failure we stay silent — the correct value reloads on next panel open.
  async function handleDeadlineChange(catId, newDate) {
    if (!newDate || !founder) return;
    try {
      await setDataRoomDeadline(founder.id, catId, newDate);
      setLocalCategories((prev) =>
        prev.map((c) => c.id === catId ? { ...c, dueDate: newDate, isCustomDeadline: true } : c)
      );
    } catch {
      // silent — will reflect correct value on next panel open
    }
  }

  return (
    <SlidePanel
      open={open}
      onClose={onClose}
      title={founder ? `${founder.first_name} ${founder.last_name}` : ''}
    >
      <div style={tabStyles.tabBar}>
        {['overview', 'notes'].map((t) => (
          <button key={t} style={activeTab === t ? tabStyles.tabActive : tabStyles.tab} onClick={() => setActiveTab(t)}>
            {t === 'overview' ? 'Overview' : 'Notes'}
          </button>
        ))}
      </div>

      {activeTab === 'overview' ? (
        <>
          {loading && <p style={styles.dim}>Loading data room…</p>}
          {!loading && data && (
            <DataRoomDetail
              data={{ ...data, categories: localCategories }}
              onDeadlineChange={handleDeadlineChange}
              founderUserId={founder?.id}
            />
          )}
        </>
      ) : (
        founder && (
          <NotesTab
            founderUserId={founder.id}
            founderName={`${founder.first_name} ${founder.last_name}`}
            page="data_room"
          />
        )
      )}
    </SlidePanel>
  );
}

function DataRoomDetail({ data, onDeadlineChange, founderUserId }) {
  const [expandedCat, setExpandedCat] = useState(null);
  const [editingDeadline, setEditingDeadline] = useState(null);
  const [downloadingCatId, setDownloadingCatId] = useState(null);

  async function handleCategoryDownload(catId, catName) {
    setDownloadingCatId(catId);
    await downloadCategoryZip(founderUserId, catId, `${catName}.zip`);
    setDownloadingCatId(null);
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.overview}>
        <CircularProgress percent={data.overallProgress} size={80} stroke={8} />
        <div>
          <p style={styles.overviewBig}>{data.completedItems} of {data.totalItems}</p>
          <p style={styles.overviewSub}>items complete</p>
        </div>
      </div>

      <p style={styles.sectionLabel}>By Category</p>
      <div style={styles.categories}>
        {data.categories.map((cat) => {
          const pct = cat.totalItems ? Math.round(cat.completedItems / cat.totalItems * 100) : 0;
          const isExpanded = expandedCat === cat.id;
          const missing = cat.items.filter((i) => i.status === 'empty');
          const uploaded = cat.items.filter((i) => i.status !== 'empty');
          return (
            <div key={cat.id} style={styles.catBlock}>
              <div style={styles.catRow} onClick={() => setExpandedCat(isExpanded ? null : cat.id)}>
                <div style={styles.catLeft}>
                  <span style={styles.catName}>{cat.name}</span>
                  <span style={styles.catCount}>{cat.completedItems}/{cat.totalItems}</span>
                </div>
                <div style={styles.catRight}>
                  <div style={styles.miniTrack}>
                    <div style={{ ...styles.miniFill, width: `${pct}%`, background: progressColor(pct) }} />
                  </div>
                  <span style={styles.catPct}>{pct}%</span>
                  <span style={styles.chevron}>{isExpanded ? '▲' : '▼'}</span>
                </div>
              </div>

              {isExpanded && (
                <div style={styles.itemList}>
                  {/* Due date row */}
                  <div style={styles.deadlineRow} onClick={(e) => e.stopPropagation()}>
                    <span style={styles.deadlineLabel}>Due:</span>
                    {editingDeadline === cat.id ? (
                      <input
                        type="date"
                        defaultValue={cat.dueDate}
                        autoFocus
                        style={styles.dateInput}
                        onChange={(e) => {
                          if (e.target.value) {
                            onDeadlineChange(cat.id, e.target.value);
                            setEditingDeadline(null);
                          }
                        }}
                        onBlur={() => setEditingDeadline(null)}
                      />
                    ) : (
                      <button
                        style={styles.deadlineValue}
                        onClick={() => setEditingDeadline(cat.id)}
                        title="Click to set a custom deadline"
                      >
                        {cat.dueDate || 'Not set'}
                        {cat.isCustomDeadline && <span style={styles.customBadge}>custom</span>}
                        <span style={styles.editIcon}>✏</span>
                      </button>
                    )}
                    <button
                      style={{ ...styles.downloadCatBtn, marginLeft: 'auto', opacity: uploaded.length === 0 ? 0.4 : 1 }}
                      onClick={() => handleCategoryDownload(cat.id, cat.name)}
                      disabled={downloadingCatId === cat.id || uploaded.length === 0}
                    >
                      {downloadingCatId === cat.id ? 'Preparing…' : 'Download'}
                    </button>
                  </div>

                  {/* Every item, uploaded or not, with an EIR sign-off control. */}
                  {cat.items.map((item) => {
                    const isUploaded = item.status !== 'empty';
                    return (
                      <div key={item.id} style={styles.itemRow}>
                        <div style={styles.itemMain}>
                          <span style={isUploaded ? styles.uploadedLabel : styles.missingLabel}>
                            {!isUploaded && <span style={styles.missingDot}>○ </span>}
                            {item.number} {item.label}
                          </span>
                          {isUploaded ? (
                            <button style={styles.viewBtn} onClick={() => openFile(item.currentFile?.url)} disabled={!item.currentFile?.url}>
                              View
                            </button>
                          ) : (
                            <span style={styles.notUploaded}>not uploaded</span>
                          )}
                        </div>
                        <ItemReviewControl founderUserId={founderUserId} item={item} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function progressColor(pct) {
  if (pct >= 80) return '#15803d';
  if (pct >= 50) return '#c66b47';
  return '#b91c1c';
}

const styles = {
  dim: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.5, fontStyle: 'italic' },

  wrap: { display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' },
  overview: { display: 'flex', alignItems: 'center', gap: 'var(--space-5)', background: 'var(--color-mint)', borderRadius: 'var(--radius-md)', padding: 'var(--space-4)' },
  overviewBig: { fontFamily: 'var(--font-accent)', fontSize: 22, fontWeight: 800, color: 'var(--color-forest)', lineHeight: 1.1 },
  overviewSub: { fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--color-forest)', opacity: 0.6, marginTop: 2 },

  sectionLabel: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: -8 },

  categories: { display: 'flex', flexDirection: 'column', gap: 4 },
  catBlock: { background: 'var(--color-mint)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' },
  catRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', width: '100%', cursor: 'pointer', gap: 12 },
  catLeft: { display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 },
  catName: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 600, color: 'var(--color-forest)', textAlign: 'left' },
  catCount: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.55, flexShrink: 0 },
  catRight: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  miniTrack: { width: 60, height: 5, background: 'var(--color-mint-dark)', borderRadius: 3, overflow: 'hidden' },
  miniFill: { height: '100%', borderRadius: 3 },
  catPct: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.7, minWidth: 28, textAlign: 'right' },
  chevron: { fontSize: 9, color: 'var(--color-forest)', opacity: 0.4 },

  itemList: { padding: '0 14px 12px' },
  allDone: { fontFamily: 'var(--font-body)', fontSize: 16, color: '#15803d', fontStyle: 'italic' },
  deadlineRow: { display: 'flex', alignItems: 'center', gap: 8, paddingTop: 8, paddingBottom: 6, borderBottom: '1px solid var(--color-mint-dark)', marginBottom: 6 },
  deadlineLabel: { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 },
  deadlineValue: { display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: 'var(--color-forest)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  customBadge: { fontSize: 11, fontWeight: 700, color: 'var(--color-rust)', background: '#fff3ee', borderRadius: 4, padding: '1px 5px', textTransform: 'uppercase', letterSpacing: '0.04em' },
  editIcon: { fontSize: 11, opacity: 0.4 },
  dateInput: { fontFamily: 'var(--font-body)', fontSize: 14, border: '1.5px solid var(--color-forest)', borderRadius: 'var(--radius-sm)', padding: '2px 6px', color: 'var(--color-forest)', background: '#fff' },
  uploadedItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, paddingTop: 6 },
  uploadedLabel: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', lineHeight: 1.4, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  viewBtn: { flexShrink: 0, fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, color: 'var(--color-rust)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' },
  missingHeader: { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.45, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 10, marginBottom: 2 },
  missingDot: { color: 'var(--color-forest)', opacity: 0.35, fontSize: 15, flexShrink: 0 },
  missingLabel: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.7, lineHeight: 1.4, flex: 1, minWidth: 0 },
  itemRow: { display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 10, paddingBottom: 4, borderBottom: '1px solid var(--color-mint-dark)' },
  itemMain: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  notUploaded: { flexShrink: 0, fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--color-forest)', opacity: 0.45, fontStyle: 'italic' },
  downloadCatBtn: { flexShrink: 0, fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700, color: 'var(--color-forest)', background: '#fff', border: '1.5px solid var(--color-mint-dark)', borderRadius: 'var(--radius-sm)', padding: '4px 10px', cursor: 'pointer' },

};

const reviewStyles = {
  wrap: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  select: { fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700, borderRadius: 'var(--radius-full)', padding: '3px 10px', cursor: 'pointer', outline: 'none' },
  note: { flex: 1, minWidth: 140, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', background: '#fff', border: '1.5px solid var(--color-mint-dark)', borderRadius: 'var(--radius-sm)', padding: '4px 8px', outline: 'none' },
};
